/**
 * CollapseSection — accordion-style wrapper for Home sections.
 *
 * Shows a 1-line summary even when collapsed so the player doesn't
 * lose at-a-glance info. Default-open state stored in localStorage
 * keyed on the section id, so player preferences persist.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  id: string;
  title: string;
  /** 1-line teaser shown next to the title even when collapsed. */
  summary?: string | undefined;
  /** Default-open state on first load. */
  defaultOpen?: boolean | undefined;
  /** Optional icon shown before the title. */
  icon?: ReactNode | undefined;
  children: ReactNode;
}

export function CollapseSection({ id, title, summary, defaultOpen = false, icon, children }: Props) {
  const key = `tt:home:collapse:${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch { /* */ }
    return defaultOpen;
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* */ }
      return next;
    });
  };

  return (
    <section className={`home-collapse${open ? ' open' : ''}`}>
      <button type="button" className="home-collapse-head" onClick={toggle} aria-expanded={open}>
        {icon && <span className="home-collapse-icon" aria-hidden="true">{icon}</span>}
        <strong>{title}</strong>
        {summary && <span className="home-collapse-summary">{summary}</span>}
        <span className="home-collapse-chevron" aria-hidden="true">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && <div className="home-collapse-body">{children}</div>}
    </section>
  );
}
